import React, { useEffect, useState } from 'react'
import moment from 'moment'

import { t } from '@/i18n'
import { TreatmentNode } from '@/apollo/graphql'
import LoadingIndicator from '@/assets/images/loading_indicator.gif'
import { IPageInfo } from '@/types'
import { getFetchMoreStatus } from '@/utils'
import { InfiniteLoader } from '@/components'
import { useDealTreatmentsQuery } from '@/apollo/graphql/queries/dealTreatments.generated'

interface Props {
  id: string
}

const pageSize = 20

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

const Treatments: React.FC<Props> = ({ id }) => {
  const [updates, setUpdates] = useState<Partial<TreatmentNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)

  const { loading, networkStatus } = useDealTreatmentsQuery({
    fetchPolicy: 'cache-first',
    variables: {
      id,
      offset: pageInfo?.offset || 0,
      first: pageSize,
    },
    onCompleted: res => {
      if (res.deal?.treatmentSet?.edges) {
        const d: Partial<TreatmentNode>[] = []
        res.deal.treatmentSet.edges.forEach(e => {
          // @ts-ignore
          if (e?.node) d.push(e.node)
        })
        setUpdates(prev => [...prev, ...d])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.deal?.treatmentSet?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  return (
    <div>
      <InfiniteLoader
        fetchMoreStatus={pageInfo.fetchMoreStatus}
        onFetchMore={() =>
          setPageInfo(prev => ({ ...prev, offset: updates.length }))
        }
      >
        <table>
          <thead>
            <tr>
              <th>{t('name')}</th>
              <th>{t('treatment_time')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="history-loading">
                <td colSpan={4}>
                  <img src={LoadingIndicator} />
                </td>
              </tr>
            )}
            {!loading && !updates.length && (
              <tr className="history-empty">
                <td colSpan={4}>{t('noData')}</td>
              </tr>
            )}
            {updates.map((datum, index) => (
              <tr key={index}>
                <td>{datum.name}</td>
                <td>
                  {datum.treatmentTime
                    ? moment(datum.treatmentTime).format('ll HH:mm:ss')
                    : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </InfiniteLoader>
    </div>
  )
}

export default Treatments
